import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Avatar,
  Chip,
  LinearProgress,
  Button,
  Divider,
  alpha,
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  Description as ReportIcon,
  LocalHospital as DoctorIcon,
  Medication as MedicationIcon,
  Add as AddIcon,
  ChevronRight as ChevronRightIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckIcon,
  TrendingUp as TrendingIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';
import { RootState, AppDispatch } from '../../store';
import { fetchOfficerDashboard } from '../../store/slices/dashboardSlice';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } }
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } }
};

const StatCard = ({ title, value, icon, color, trend, delay = 0 }: { title: string; value: number; icon: React.ReactNode; color: string; trend?: string; delay?: number }) => (
  <motion.div variants={itemVariants} initial="hidden" animate="visible" transition={{ delay }}>
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 3,
        background: '#fff',
        border: '1px solid #e5e7eb',
        position: 'relative',
        '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" color="text.secondary" fontWeight={500}>{title}</Typography>
          <Typography variant="h4" fontWeight={700} color="text.primary">{value.toLocaleString()}</Typography>
          {trend && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <TrendingIcon sx={{ fontSize: 14, color: '#10b981' }} />
              <Typography variant="caption" color="#10b981" fontWeight={600}>{trend}</Typography>
            </Box>
          )}
        </Box>
        <Avatar sx={{ width: 48, height: 48, bgcolor: alpha(color, 0.1), color }}>{icon}</Avatar>
      </Box>
    </Paper>
  </motion.div>
);

const QuickBtn = ({ icon, label, onClick, color }: { icon: React.ReactNode; label: string; onClick: () => void; color: string }) => (
  <Button
    variant="outlined"
    startIcon={icon}
    onClick={onClick}
    sx={{
      borderRadius: 2,
      textTransform: 'none',
      fontWeight: 600,
      borderColor: '#e5e7eb',
      color: '#374151',
      '&:hover': { borderColor: color, color, bgcolor: alpha(color, 0.04) }
    }}
  >
    {label}
  </Button>
);

const OfficerDashboardPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { officerDashboard, loading } = useSelector((state: RootState) => state.dashboard);

  useEffect(() => {
    dispatch(fetchOfficerDashboard());
  }, [dispatch]);

  if (loading || !officerDashboard) {
    return <Box sx={{ p: 4 }}><LinearProgress /></Box>;
  }

  const { stats, upcomingAppointments, recentVisitReports } = officerDashboard;
  const phaseTwo = officerDashboard.phaseTwo;

  const statusColors = {
    PENDING: { bg: '#fef3c7', color: '#92400e' },
    CONFIRMED: { bg: '#d1fae5', color: '#065f46' },
    COMPLETED: { bg: '#d1fae5', color: '#065f46' },
    CANCELLED: { bg: '#fee2e2', color: '#991b1b' },
  };

  const statsData = [
    { title: 'Appointments', value: stats.totalAppointments || 0, icon: <CalendarIcon />, color: '#2563eb', trend: '+12%' },
    { title: 'Reports', value: stats.totalVisitReports || 0, icon: <ReportIcon />, color: '#059669', trend: '+5%' },
    { title: 'Pending', value: stats.pendingAppointments || 0, icon: <TimeIcon />, color: '#f59e0b' },
    { title: 'Completed', value: stats.completedAppointments || 0, icon: <CheckIcon />, color: '#10b981', trend: '+8%' },
  ];

  return (
    <Box sx={{ p: 2, flex: 1, overflow: 'auto', bgcolor: 'transparent' }}>
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h5" fontWeight={700} color="#111827">Dashboard</Typography>
              <Typography variant="body2" color="#6b7280">Welcome back! Here's your healthcare overview.</Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/appointments/book')}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3, py: 1 }}
            >
              Book Appointment
            </Button>
          </Box>
        </motion.div>

        {/* Stats */}
        <motion.div variants={containerVariants} initial="hidden" animate="visible">
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {statsData.map((stat, i) => (
              <Grid item xs={6} sm={3} key={i}>
                <StatCard {...stat} delay={i * 0.05} />
              </Grid>
            ))}
          </Grid>
        </motion.div>

        {/* Content */}
        <Grid container spacing={3}>
          {/* Appointments */}
          <Grid item xs={12} md={6}>
            <motion.div variants={itemVariants} initial="hidden" animate="visible">
              <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e5e7eb' }}>
                <Box sx={{ p: 2, bgcolor: 'transparent' , borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} color="#111827">Upcoming Appointments</Typography>
                    <Typography variant="caption" color="#6b7280">Your scheduled visits</Typography>
                  </Box>
                  <Button size="small" endIcon={<ChevronRightIcon />} onClick={() => navigate('/appointments')} sx={{ textTransform: 'none', fontWeight: 600 }}>
                    View All
                  </Button>
                </Box>
                <Box sx={{ p: 2, maxHeight: 320, overflow: 'auto' }}>
                  {upcomingAppointments?.length > 0 ? (
                    upcomingAppointments.slice(0, 4).map((appt, i) => (
                      <motion.div key={appt.id || i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}>
                        <Box
                          sx={{ p: 1.5, borderRadius: 2, cursor: 'pointer', '&:hover': { bgcolor: 'transparent' } }}
                          onClick={() => navigate(`/appointments/${appt.id}`)}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{ width: 40, height: 40, bgcolor: 'transparent' , color: '#2563eb' }}><DoctorIcon sx={{ fontSize: 18 }} /></Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={600} color="#111827">Dr. {appt.doctorName}</Typography>
                              <Typography variant="caption" color="#6b7280">{appt.appointmentDate} • {appt.startTime}</Typography>
                            </Box>
                            <Chip label={appt.status} size="small" sx={{ bgcolor: (statusColors as Record<string, { bg: string; color: string }>)[appt.status]?.bg || '#f3f4f6', color: (statusColors as Record<string, { bg: string; color: string }>)[appt.status]?.color || '#374151', fontWeight: 600, fontSize: '0.7rem' }} />
                          </Box>
                        </Box>
                        {i < Math.min(upcomingAppointments.length - 1, 3) && <Divider />}
                      </motion.div>
                    ))
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <CalendarIcon sx={{ fontSize: 40, color: '#d1d5db', mb: 1 }} />
                      <Typography color="#6b7280" variant="body2">No appointments scheduled</Typography>
                      <Button variant="text" size="small" sx={{ mt: 1, textTransform: 'none' }} onClick={() => navigate('/appointments/book')}>Book now</Button>
                    </Box>
                  )}
                </Box>
              </Paper>
            </motion.div>
          </Grid>

          {/* Reports */}
          <Grid item xs={12} md={6}>
            <motion.div variants={itemVariants} initial="hidden" animate="visible" transition={{ delay: 0.1 }}>
              <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e5e7eb' }}>
                <Box sx={{ p: 2, bgcolor: 'transparent' , borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} color="#111827">Medical Reports</Typography>
                    <Typography variant="caption" color="#6b7280">Your health records</Typography>
                  </Box>
                  <Button size="small" endIcon={<ChevronRightIcon />} onClick={() => navigate('/visit-reports')} sx={{ textTransform: 'none', fontWeight: 600 }}>
                    View All
                  </Button>
                </Box>
                <Box sx={{ p: 2, maxHeight: 320, overflow: 'auto' }}>
                  {recentVisitReports?.length > 0 ? (
                    recentVisitReports.slice(0, 4).map((report, i) => (
                      <motion.div key={report.id || i} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}>
                        <Box
                          sx={{ p: 1.5, borderRadius: 2, cursor: 'pointer', '&:hover': { bgcolor: 'transparent' } }}
                          onClick={() => navigate(`/visit-reports/${report.id}`)}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{ width: 40, height: 40, bgcolor: 'transparent' , color: '#92400e' }}><ReportIcon sx={{ fontSize: 18 }} /></Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={600} color="#111827">Dr. {report.doctorName}</Typography>
                              <Typography variant="caption" color="#6b7280">{report.visitDate} • {report.diagnosis || 'Consultation'}</Typography>
                            </Box>
                            {report.hasPrescription && <Avatar sx={{ width: 24, height: 24, bgcolor: 'transparent' }}><MedicationIcon sx={{ fontSize: 14, color: '#2563eb' }} /></Avatar>}
                          </Box>
                        </Box>
                        {i < Math.min(recentVisitReports.length - 1, 3) && <Divider />}
                      </motion.div>
                    ))
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <ReportIcon sx={{ fontSize: 40, color: '#d1d5db', mb: 1 }} />
                      <Typography color="#6b7280" variant="body2">No reports available</Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </motion.div>
          </Grid>

          {/* Quick Actions */}
          <Grid item xs={12}>
            <motion.div variants={itemVariants} initial="hidden" animate="visible" transition={{ delay: 0.2 }}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2" fontWeight={600} color="#111827" sx={{ mr: 1 }}>Quick Actions</Typography>
                <QuickBtn icon={<CalendarIcon />} label="Appointments" onClick={() => navigate('/appointments')} color="#2563eb" />
                <QuickBtn icon={<ReportIcon />} label="Medical Records" onClick={() => navigate('/visit-reports')} color="#059669" />
                <QuickBtn icon={<DoctorIcon />} label="Find Doctors" onClick={() => navigate('/doctors')} color="#7c3aed" />
              </Paper>
            </motion.div>
          </Grid>

          {/* ===== Phase-2 widgets (additive) ===== */}
          <Grid item xs={12}>
            <motion.div variants={itemVariants} initial="hidden" animate="visible" transition={{ delay: 0.25 }}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid #e5e7eb' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Typography variant="subtitle1" fontWeight={700} color="#111827">Health Management</Typography>
                  <Button size="small" endIcon={<ChevronRightIcon />} onClick={() => navigate('/officer/timeline')} sx={{ textTransform: 'none', fontWeight: 600 }}>Timeline</Button>
                </Box>
                <Grid container spacing={2}>
                  {[
                    { label: 'Active Insurance', value: phaseTwo?.activeInsuranceCount ?? 0, color: '#10B981', path: '/officer/insurance' },
                    { label: 'Active Admissions', value: phaseTwo?.activeAdmissions ?? 0, color: '#F59E0B', path: '/officer/admissions' },
                    { label: 'Active Investigations', value: phaseTwo?.activeInvestigations ?? 0, color: '#EC4899', path: '/officer/investigations' },
                    { label: 'Upcoming Blood Tests', value: phaseTwo?.upcomingBloodTests ?? 0, color: '#EF4444', path: '/officer/blood-tests' },
                  ].map((w) => (
                    <Grid item xs={6} sm={3} key={w.label}>
                      <Box onClick={() => navigate(w.path)} sx={{ p: 2, borderRadius: 2, border: '1px solid #e5e7eb', cursor: 'pointer', '&:hover': { boxShadow: 1 } }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: w.color, mb: 1 }} />
                        <Typography variant="h5" fontWeight={700} color="#111827">{w.value}</Typography>
                        <Typography variant="caption" color="#6b7280">{w.label}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                <Box sx={{ mt: 2.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => navigate('/officer/medical-reports')} sx={{ textTransform: 'none' }}>Medical Reports</Button>
                  <Button size="small" variant="outlined" startIcon={<MedicationIcon />} onClick={() => navigate('/prescription-documents')} sx={{ textTransform: 'none' }}>Prescriptions</Button>
                  <Button size="small" variant="outlined" startIcon={<ReportIcon />} onClick={() => navigate('/officer/documents')} sx={{ textTransform: 'none' }}>Documents</Button>
                  <Button size="small" variant="outlined" startIcon={<DoctorIcon />} onClick={() => navigate('/officer/admissions')} sx={{ textTransform: 'none' }}>Request Admission</Button>
                </Box>
              </Paper>
            </motion.div>
          </Grid>

          {/* Recent medical reports (Phase-2) */}
          <Grid item xs={12} md={6}>
            <motion.div variants={itemVariants} initial="hidden" animate="visible" transition={{ delay: 0.3 }}>
              <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e5e7eb' }}>
                <Box sx={{ p: 2, borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle1" fontWeight={600} color="#111827">Recent Medical Reports</Typography>
                  <Button size="small" endIcon={<ChevronRightIcon />} onClick={() => navigate('/officer/medical-reports')} sx={{ textTransform: 'none', fontWeight: 600 }}>View All</Button>
                </Box>
                <Box sx={{ p: 2, maxHeight: 260, overflow: 'auto' }}>
                  {phaseTwo?.recentMedicalReports?.length ? (
                    phaseTwo.recentMedicalReports.slice(0, 5).map((r, i) => (
                      <Box key={r.id || i} sx={{ py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" fontWeight={600} color="#111827">{r.reportTitle}</Typography>
                          <Typography variant="caption" color="#6b7280">{r.reportTypeDisplayName || r.reportType} · {r.reportDate}</Typography>
                        </Box>
                        <Chip size="small" label={r.statusDisplayName || r.status} sx={{ bgcolor: '#eff6ff', color: '#1d4ed8' }} />
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2" color="#6b7280" sx={{ py: 2, textAlign: 'center' }}>No reports uploaded yet.</Typography>
                  )}
                </Box>
              </Paper>
            </motion.div>
          </Grid>

          {/* Health timeline (Phase-2, compact) */}
          <Grid item xs={12} md={6}>
            <motion.div variants={itemVariants} initial="hidden" animate="visible" transition={{ delay: 0.35 }}>
              <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e5e7eb' }}>
                <Box sx={{ p: 2, borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle1" fontWeight={600} color="#111827">Health Timeline</Typography>
                  <Button size="small" endIcon={<ChevronRightIcon />} onClick={() => navigate('/officer/timeline')} sx={{ textTransform: 'none', fontWeight: 600 }}>Full View</Button>
                </Box>
                <Box sx={{ p: 2, maxHeight: 260, overflow: 'auto' }}>
                  {phaseTwo?.healthTimeline?.length ? (
                    phaseTwo.healthTimeline.slice(0, 5).map((e, i) => (
                      <Box key={e.id || i} sx={{ display: 'flex', gap: 1.5, py: 0.75 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#8B5CF6', mt: 0.75 }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight={600} color="#111827">{e.eventTitle}</Typography>
                          <Typography variant="caption" color="#6b7280">{e.eventCategoryDisplayName || e.eventCategory} · {e.eventDate}</Typography>
                        </Box>
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2" color="#6b7280" sx={{ py: 2, textAlign: 'center' }}>No timeline events yet.</Typography>
                  )}
                </Box>
              </Paper>
            </motion.div>
          </Grid>
        </Grid>
    </Box>
  );
};

export default OfficerDashboardPage;